import { selectors } from '@/Store';
import { getApiHeaders } from '@/Utils/Sentry';
import { isoTimeToSeconds } from '@/Utils/TimeFormat';
import { WSClient, createWSClient } from '@/Utils/wsClient';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, from, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/browser';
import { WebSocketConfiguration } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { T, equals } from 'ramda';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from 'react-use';

const MAX_RETRY_COUNT = 5;
const MIN_PING_INTERVAL = 60 * 1000;
const MAX_RETRY_WAIT = 3600 * 1000; // 1 hour
const DELAY_TIME_IN_SECONDS = 2;
const secondsToMiliseconds = (seconds: number) => {
  return seconds * 1000;
};

const useApollo = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const pingIntervalRef = useRef<any>(null);
  const webSocketConfigState = useSelector(selectors.auth.webSocketConfig);
  const [webSocketConfig, setWebSocketConfig] = useState<WebSocketConfiguration | undefined>();
  const previousWebSocketConfigState = usePrevious(webSocketConfigState);
  const [client, setClient] = useState<ApolloClient<any> | null>(null);
  const [clientInitTimeout, setclientInitTimeout] = useState(false);

  useEffect(() => {
    // if the client is already initialized and the config is the same, do nothing
    if (!equals(webSocketConfigState, previousWebSocketConfigState)) {
      setWebSocketConfig(webSocketConfigState);
    }
  }, [webSocketConfigState, previousWebSocketConfigState]);

  const asyncAuthLink = setContext(async () => {
    let token: string | null = null;
    if (isAuthenticated) {
      token = await getAccessTokenSilently();
    }
    const headers: any = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
      ...getApiHeaders(),
    };
    return {
      headers,
    };
  });

  const sentryErrorLink = onError(({ operation, graphQLErrors, networkError }) => {
    Sentry.withScope(scope => {
      scope.setTransactionName(operation.operationName);
      scope.setContext('apolloGraphQLOperation', {
        operationName: operation.operationName,
        variables: operation.variables,
        extensions: operation.extensions,
        query: operation.query,
      });

      graphQLErrors?.forEach(gqlError => {
        const error = new Error(gqlError.message);
        error.stack = operation.getContext().clientStackTrace;
        Sentry.captureException(error, {
          fingerprint: ['{{ default }}', '{{ transaction }}'],
          contexts: {
            apolloGraphQLError: {
              gqlError,
              message: gqlError.message,
              extensions: gqlError.extensions,
            },
          },
        });
      });

      if (networkError) {
        const error = new Error(networkError.message);
        error.stack = operation.getContext().clientStackTrace;
        Sentry.captureException(error, {
          contexts: {
            apolloNetworkError: {
              error: networkError,
              extensions: (networkError as any).extensions,
            },
          },
        });
      }
    });
  });

  const httpLink = new HttpLink({
    uri: `${process.env.EXP_API_URL}/exp/graph`,
  });

  // Retry link to handle retries on network failures
  const retryLink = new RetryLink({
    attempts: count => count <= MAX_RETRY_COUNT, // Retry up to 5 times
    delay: () => DELAY_TIME_IN_SECONDS * 1000, // Exponential backoff with max delay
  });
  const createTimeoutPromise = (delay: number): Promise<void> => {
    return new Promise(resolve => {
      setTimeout(resolve, delay);
    });
  };
  const clearPingInterval = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  };
  const initWSClient = useCallback(
    (websocketEndpoint: string, pingInterval): WSClient => {
      const wsClient = createWSClient({
        url: websocketEndpoint,

        shouldRetry: () => T,
        retryAttempts: Infinity,
        retryWait: async (retries: number) => {
          /**
           * Randomized exponential backoff for retrying WebSocket connections
           */
          let retryDelay = 60 * 1000; // 1 minute base
          for (let i = 0; i < retries; i += 1) {
            retryDelay *= 2;
          }
          retryDelay = Math.min(retryDelay, MAX_RETRY_WAIT); // MAX_RETRY_WAIT = 1 hour
          retryDelay += Math.floor(Math.random() * (3000 - 300) + 300); // Jitter: 300ms–3s
          await createTimeoutPromise(retryDelay);
        },

        connectionParams: async () => {
          const token = await getAccessTokenSilently();
          return {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...getApiHeaders(),
          };
        },
      });
      wsClient.on('connected', () => {
        // eslint-disable-next-line no-console
        console.log('---[WS] connected');
        clearPingInterval(); // always clear before starting new
        const pingIntervalMax = Math.max(secondsToMiliseconds(pingInterval), MIN_PING_INTERVAL);
        pingIntervalRef.current = setInterval(() => {
          wsClient?.ping();
        }, pingIntervalMax);
      });
      wsClient.on('closed', () => {
        clearPingInterval();
      });

      wsClient.on('error', (err: unknown) => {
        /*
         * When a WebSocket error occurs in browsers (e.g., due to network failure, DNS error,
         * CORS rejection, or invalid endpoint), the browser triggers the `onerror` handler.
         *
         * However, for security reasons, the browser does NOT expose the actual error details.
         * Instead, it passes a generic `Event` object (e.g., Event { isTrusted: true }).
         *
         * This kind of error contains no useful debugging information and is often non-actionable,
         * so we intentionally suppress reporting it to Sentry to reduce noise.
         */
        if (err instanceof Event && err.type === 'error') {
          return;
        }
        let errorToCapture: Error;
        if (err instanceof Error) {
          errorToCapture = err;
        } else if (typeof err === 'object' && err !== null) {
          errorToCapture = new Error(`WebSocket error: ${JSON.stringify(err)}`);
        } else {
          errorToCapture = new Error(`WebSocket error: ${String(err)}`);
        }
        Sentry.captureException(errorToCapture, {
          contexts: {
            webSocketError: {
              originalError: err,
            },
          },
        });
      });

      return wsClient;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initClient = useCallback(
    (wsClient: WSClient | null): ApolloClient<any> => {
      let splitLink: ApolloLink | null = null;
      if (wsClient) {
        splitLink = split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
            );
          },
          new GraphQLWsLink(wsClient),
          asyncAuthLink.concat(httpLink),
        );
      } else {
        splitLink = asyncAuthLink.concat(httpLink);
      }
      return new ApolloClient({
        link: from([sentryErrorLink, retryLink, splitLink]),
        cache: new InMemoryCache(),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => {
    if (webSocketConfig) {
      const websocketEndpoint = webSocketConfig.endpoint;
      const pingInterval = isoTimeToSeconds(webSocketConfig.pingInterval);
      let wsClient: WSClient | null = null;
      if (isAuthenticated) {
        wsClient = initWSClient(websocketEndpoint, pingInterval);
      }
      const apolloClient = initClient(wsClient);
      setClient(apolloClient);
      return () => {
        apolloClient?.stop();
        wsClient?.dispose();
      };
    }
    return undefined;
  }, [webSocketConfig, initClient, initWSClient, isAuthenticated]);
  useEffect(() => {
    if (client) {
      setclientInitTimeout(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setclientInitTimeout(true);
    }, 5000);
    return () => {
      clearTimeout(timer);
    };
  }, [client]);
  return { client, clientInitTimeout };
};

export default useApollo;
